import { useCallback, useContext, useEffect, useState } from 'react';
import { ModalContext, BackdropLoadingContext } from './modalContext';
import { RMModalProps } from './RMModal';
import { ModalType } from './constants';

export const useModalHandlers = () => {
  const modalContext = useContext(ModalContext);

  const hideModal = useCallback(() => {
    modalContext.setModalDetails((prevState) => ({
      ...prevState,
      showModal: false,
    }));
  }, [modalContext]);

  const showModal = useCallback(
    (props: Omit<RMModalProps, 'showModal' | 'setShowModal'>) => {
      modalContext.setModalDetails({
        type: ModalType.Alert,
        showModal: true,
        setShowModal: hideModal,
        ...props,
      });
    },
    [modalContext, hideModal],
  );

  const showAwaitableModal = useCallback(
    (
      props: Omit<RMModalProps, 'showModal' | 'setShowModal'> & {
        resolveModalOnHide?: boolean;
      },
    ) => {
      const { onOk, onHide, resolveModalOnHide = false, ...rest } = props;
      return new Promise<void>((resolve) => {
        modalContext.setModalDetails({
          type: ModalType.Alert,
          showModal: true,
          setShowModal: () => {
            hideModal();
            resolve();
          },
          onOk: () => {
            if (onOk) {
              onOk();
            }
            resolve();
          },
          onHide: () => {
            if (onHide) {
              onHide();
            }
            if (resolveModalOnHide) {
              resolve();
            }
          },
          ...rest,
        });
      });
    },
    [modalContext, hideModal],
  );

  const showLoadingModal = useCallback(
    (text: string) => {
      modalContext.setModalDetails({
        type: ModalType.Loading,
        showModal: true,
        text,
      });
    },
    [modalContext],
  );
  return { showModal, showAwaitableModal, hideModal, showLoadingModal };
};

export const useBackdropLoading = () => {
  const { showBackdropLoading, showBackdropDescription } =
    useContext(BackdropLoadingContext);
  const [isBackdropOpen, setOpen] = useState(false);

  useEffect(() => {
    showBackdropLoading(isBackdropOpen);
  }, [isBackdropOpen, showBackdropLoading]);

  const showBackdrop = useCallback(
    (desc: string) => {
      showBackdropDescription(desc);
      setOpen(true);
    },
    [showBackdropDescription],
  );

  const hideBackdrop = useCallback(() => {
    setOpen(false);
    showBackdropDescription('Loading...');
  }, [showBackdropDescription]);

  return { showBackdrop, hideBackdrop, isBackdropOpen };
};
